import { z } from "zod/v4";
import { paginationSearchSchema } from "#/modules/util.pagination/search.pagination";
import { nuqsBoolean } from "#/modules/util.pagination/search.nuqs";
import {
  createOverviewSearchSchema,
  overviewFilterOperator,
  overviewFilterValues,
} from "#/modules/util.overview/schema.search";

const sortOrder = ["ascending", "descending"] as const;

/**
 * Search params schema for activities overview: searchTerm, orderBy, orderDirection, includeDefaults, pagination.
 */
export const activitiesSearchSchema = paginationSearchSchema.extend({
  searchTerm: z.string().default(""),
  orderBy: z.string().default("leerlijn, beweegthema, isDefault, name"),
  orderDirection: z.enum(sortOrder).default("ascending").catch("ascending"),
  includeDefaults: nuqsBoolean(true),
});

/** Default values for activities search params; values not explicitly set will be omitted from URL. */
export const ACTIVITIES_SEARCH_DEFAULTS = {
  searchTerm: "",
  orderBy: "leerlijn, beweegthema, isDefault, name",
  orderDirection: "ascending" as const,
  includeDefaults: true,
  page: 1,
  pageSize: 40,
};

export type ActivitiesSearch = z.infer<typeof activitiesSearchSchema>;

/**
 * Search params schema for the admin activities overview: searchTerm, sort, includeSchools
 * toggle, leerlijn/beweegthema/leerjaarCombo filters (enum-name arrays + in/notIn operator),
 * isPublished filter, pagination.
 */
// leerjaarCombo is not sortable: it is a typed contains-filter backend-side, not a Gridify map.
const adminActivities = createOverviewSearchSchema({
  orderBy: "leerlijn, beweegthema, name",
  extra: {
    // Admins manage the default curriculum; school-owned reeksen are opt-in via the toggle.
    includeSchools: nuqsBoolean(false),
    leerlijn: overviewFilterValues(),
    leerlijnOperator: overviewFilterOperator(),
    beweegthema: overviewFilterValues(),
    beweegthemaOperator: overviewFilterOperator(),
    leerjaarCombo: overviewFilterValues(),
    leerjaarComboOperator: overviewFilterOperator(),
    isPublished: overviewFilterValues(),
  },
});

export const adminActivitiesSearchSchema = adminActivities.schema;
export const ADMIN_ACTIVITIES_SEARCH_DEFAULTS = adminActivities.defaults;

export type AdminActivitiesSearch = z.infer<typeof adminActivitiesSearchSchema>;
